import type { Flag } from "@medplum/fhirtypes";
import { FLAG_ENTITY_ID_SYSTEM, getTRPCClient, syncKey } from "@repo/api";
import {
  type FlagIdInput,
  flagIdInputSchema,
  flagIdSchema,
  patientIdSchema,
  practitionerIdSchema,
  type RaiseFlagInput,
  raiseFlagInputSchema,
  type UpdateFlagInput,
  updateFlagInputSchema,
  visitIdSchema,
} from "@repo/api/schemas";
import type { FlagId, PatientId, PractitionerId, VisitId } from "@repo/domain";
import { FLAG_CATEGORY_SYSTEM } from "@repo/fhir-systems";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import { optimisticPatientReference, patientDomainId } from "./_patient-reference";
import { optimisticPractitionerReference, practitionerDomainId } from "./_practitioner-reference";
import { optimisticVisitExecutionReference, visitDomainId } from "./_visit-reference";
import type { CodeableConcept } from "./shared/types";

/**
 * Flag — normalized read-model for patient safety/alert banners.
 * Read-only for now; the raise/resolve flow arrives with the flag domain.
 *
 * FHIR Flag carries no severity field, so "high priority" is derived: a
 * `safety`-category flag outranks others, then most-recent active wins. Only
 * `active` flags within their `period` should surface — use `isFlagActive`.
 */
export interface FlagDto {
  flagId: FlagId;
  patientId: PatientId;

  status: "active" | "inactive" | "entered-in-error";

  /** The alert concept/text shown to the clinician. */
  code: CodeableConcept;
  /** Classification(s) — e.g. safety, clinical, administrative. */
  category?: CodeableConcept[];

  periodStart?: string;
  periodEnd?: string;

  authorPractitionerId?: PractitionerId;
  visitId?: VisitId;
}

const COLLECTION = "flag";

function flatCodeable(
  c: { coding?: Array<{ system?: string; code?: string; display?: string }>; text?: string } | undefined,
): CodeableConcept | undefined {
  if (!c) return undefined;
  const first = c.coding?.[0];
  const out = {
    ...(first?.system ? { system: first.system } : {}),
    ...(first?.code ? { code: first.code } : {}),
    ...(first?.display ? { display: first.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function domainFlagId(flag: Flag): string | undefined {
  return flag.identifier?.find((identifier) => identifier.system === FLAG_ENTITY_ID_SYSTEM)?.value;
}

function toDto(f: Flag): FlagDto | undefined {
  const flagId = domainFlagId(f);
  if (!flagId) return undefined;
  const parsedFlagId = flagIdSchema.safeParse(flagId);
  const parsedPatientId = patientIdSchema.safeParse(patientDomainId(f.subject));
  if (!parsedFlagId.success || !parsedPatientId.success) return undefined;
  const parsedAuthorId = practitionerIdSchema.safeParse(practitionerDomainId(f.author));
  const parsedVisitId = visitIdSchema.safeParse(visitDomainId(f.encounter));
  const code = flatCodeable(f.code) ?? {};
  const category = (f.category ?? []).map((c) => flatCodeable(c)).filter((c): c is CodeableConcept => !!c);
  const status = f.status === "inactive" || f.status === "entered-in-error" ? f.status : "active";
  return {
    flagId: parsedFlagId.data,
    patientId: parsedPatientId.data,
    status,
    code,
    ...(category.length ? { category } : {}),
    ...(f.period?.start ? { periodStart: f.period.start } : {}),
    ...(f.period?.end ? { periodEnd: f.period.end } : {}),
    ...(parsedAuthorId.success ? { authorPractitionerId: parsedAuthorId.data } : {}),
    ...(parsedVisitId.success ? { visitId: parsedVisitId.data } : {}),
  };
}

/** Human-readable alert label for a flag. */
export function flagLabel(f: FlagDto): string {
  return f.code.text ?? f.code.display ?? f.code.code ?? "Flag";
}

/** True when the flag is a `safety`-category alert. */
export function isSafetyFlag(f: FlagDto): boolean {
  return (f.category ?? []).some((c) => c.system === FLAG_CATEGORY_SYSTEM && c.code === "safety");
}

/** Active = status `active` and the period (if any) hasn't ended. */
export function isFlagActive(f: FlagDto, nowMs: number = Date.now()): boolean {
  if (f.status !== "active") return false;
  if (f.periodEnd) {
    const end = Date.parse(f.periodEnd);
    if (!Number.isNaN(end) && end < nowMs) return false;
  }
  return true;
}

/**
 * Picks the single highest-priority active flag for the summary banner:
 * safety flags first, then the most recently started.
 */
export function selectBannerFlag(flags: FlagDto[], nowMs: number = Date.now()): FlagDto | undefined {
  const active = flags.filter((f) => isFlagActive(f, nowMs));
  if (active.length === 0) return undefined;
  return [...active].sort((a, b) => {
    const safety = Number(isSafetyFlag(b)) - Number(isSafetyFlag(a));
    if (safety !== 0) return safety;
    return (b.periodStart ? Date.parse(b.periodStart) : 0) - (a.periodStart ? Date.parse(a.periodStart) : 0);
  })[0];
}

interface UseFlagsParams {
  patientId?: string;
}

interface UseFlagsResult {
  flags: FlagDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function useFlags(params?: UseFlagsParams): UseFlagsResult {
  const collection = useCollection(COLLECTION);
  const patientId = params?.patientId;

  const result = useLiveQuery({
    queryKey: [collection.id, "flags", patientId ?? null],
    query: (q) => {
      if (patientId) {
        return q
          .from({ f: collection })
          .where(({ f }) => eq(f.subject?.identifier?.value, patientId))
          .fn.select(({ f }) => ({ dto: toDto(f) }));
      }
      return undefined;
    },
  });

  return {
    flags: ((result.data as Array<{ dto?: FlagDto }> | undefined) ?? []).flatMap((row) => (row.dto ? [row.dto] : [])),
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

// ── Write-side intents (optimistic) ──

const flags = () => getDataStoreClient().getCollection<Flag, string>(COLLECTION);

function persistedFlagByDomainId(flagId: string): Flag | undefined {
  return flags().get(flagId);
}

/** Intent: "raise a safety/admin flag on a patient". */
export const raiseFlag = defineWrite<RaiseFlagInput>({
  optimistic: (params) => {
    const input = raiseFlagInputSchema.parse(params);
    const optimisticFlagId = input.id;
    const record = {
      resourceType: "Flag",
      identifier: [{ system: FLAG_ENTITY_ID_SYSTEM, value: optimisticFlagId }],
      status: "active",
      code: input.code,
      subject: optimisticPatientReference(input.patient.id),
      category: input.category,
      period: input.period ?? { start: new Date().toISOString() },
      author: input.author ? optimisticPractitionerReference(input.author.id) : undefined,
      encounter: input.visitId ? optimisticVisitExecutionReference(input.visitId) : undefined,
    } as Flag;
    flags().insert(record);
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.flag.raise.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "resolve a flag" — status→inactive, stamp period.end. */
export const resolveFlag = defineWrite<FlagIdInput>({
  optimistic: (params) => {
    const input = flagIdInputSchema.parse(params);
    const collection = flags();
    const resource = persistedFlagByDomainId(input.flagId);
    if (!resource) throw new DomainResultError("This flag no longer exists. Refresh and try again.");
    const end = new Date().toISOString();
    // Tracked write — registers the mutation so mutationFn fires.
    collection.update(syncKey(resource), (draft) => {
      draft.status = "inactive";
      draft.period = { ...(draft.period ?? {}), end };
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.flag.resolve.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "update a flag's alert code or category". */
export const updateFlag = defineWrite<UpdateFlagInput>({
  optimistic: (params) => {
    const input = updateFlagInputSchema.parse(params);
    const collection = flags();
    const resource = persistedFlagByDomainId(input.flagId);
    if (!resource) throw new DomainResultError("This flag no longer exists. Refresh and try again.");
    const changes: Partial<Flag> = {};
    if (input.code !== undefined) changes.code = input.code;
    if (input.category !== undefined) changes.category = input.category;
    // Tracked write — registers the mutation so mutationFn fires.
    collection.update(syncKey(resource), (draft) => {
      Object.assign(draft, changes);
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.flag.update.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
